import React, { useEffect, useState } from "react";
import {
    Box,
    MenuItem,
    Grid,
    TextField,
    Button,
    Typography,
    FormControlLabel,
    RadioGroup,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";

import {
    makePUTRequestOnUserService,
} from "../../Api/api";

import {
    incomeRanges,
    qualification,
    differentlyAbledStatus,
    typeOfImpairment,
} from "../Users/PersonalDetails";

import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const identificationTypes = ["TIN", "Equivalent"];

const emptyTaxResidencyRow = {
    account_holder_name: "",
    account_holder_details: "",
    country_of_tax_residency: "",
    tax_identification_number: "",
    identification_type: "",
    is_us_citizen_or_green_card_holder: false,
};

interface TaxRowErrors {
    account_holder_name?: string;
    account_holder_details?: string;
    country_of_tax_residency?: string;
    tax_identification_number?: string;
    identification_type?: string;
}

const validateTaxRow = (row: any): TaxRowErrors => {
    const errors: TaxRowErrors = {};

    if (!row.account_holder_name?.trim()) {
        errors.account_holder_name = "Required";
    }

    if (!row.account_holder_details?.trim()) {
        errors.account_holder_details = "Required";
    }

    if (!row.country_of_tax_residency?.trim()) {
        errors.country_of_tax_residency = "Required";
    }

    if (!row.tax_identification_number?.trim()) {
        errors.tax_identification_number = "Required";
    } else if (
        !/^[A-Za-z0-9-]{4,20}$/.test(
            row.tax_identification_number.trim()
        )
    ) {
        errors.tax_identification_number =
            "Enter a valid TIN (4-20 alphanumeric chars)";
    }

    if (!row.identification_type) {
        errors.identification_type = "Required";
    }

    return errors;
};

export const PersonalDetails = ({
    personal_details,
    fetchKycDocs,
    currentUser,
    user_type,
    setValue,
    getErrorMessage,
    accountTypeState,
}: any) => {
    const [loading, setLoading] = useState(false);

    const toastr = require("toastr");
    const isNRI =
        accountTypeState === "individual_non_resident" ||
        currentUser?.accountType === "individual_non_resident";

    const [formData, setFormData] = useState<any>({
        maritalStatus: "",
        maidenName: "",
        gender: "",
        occupation: "",
        politicallyExposed: "",
        annualIncome: "",
        motherName: "",
        qualification: "",
        differentlyAbledStatus: "",
        typeOfImpairment: "",
        percentageOfImpairment: "",
        UDIDNumber: "",
        birthCity: "",
        birthCountry: "",
        nationality: "",
        wetSignImage: null,
        passportPhoto: null,
        sebi_action: null,
        investment_trading_experience: "",
        status: "",
        is_merchant_navy_nri: false,
        merchant_navy_document_type: "",
        merchant_navy_document: null,
        tax_resident_other_country: false,
        tax_residency_details: [],
    });

    const [taxRowErrors, setTaxRowErrors] = useState<TaxRowErrors[]>([]);
    const [taxSectionError, setTaxSectionError] = useState("");

    useEffect(() => {
        if (!personal_details) {
            return;
        }

        setFormData((prev: any) => ({
            ...prev,

            maritalStatus: personal_details?.maritalStatus
                ? personal_details.maritalStatus
                    .charAt(0)
                    .toUpperCase() +
                personal_details.maritalStatus.slice(1).toLowerCase()
                : "",

            maidenName: personal_details?.maidenName || "",

            gender: personal_details?.gender || "",

            occupation: personal_details?.occupation || "",

            politicallyExposed:
                personal_details?.politicallyExposed === true
                    ? "Yes"
                    : personal_details?.politicallyExposed === false
                        ? "No"
                        : "",

            annualIncome: personal_details?.annualIncome || "",

            motherName: personal_details?.mothersName || "",

            qualification: personal_details?.qualification || "",

            differentlyAbledStatus:
                personal_details?.differentlyAbledStatus || "",

            typeOfImpairment:
                personal_details?.typeOfImpairment || "",

            percentageOfImpairment:
                personal_details?.percentageOfImpairment || "",

            UDIDNumber:
                personal_details?.UDIDNumber || "",

            birthCity:
                personal_details?.birthCity || "",

            birthCountry:
                personal_details?.birthCountry || "",

            nationality:
                personal_details?.nationality || "",

            wetSignImage:
                personal_details?.wetSignImage || null,

            passportPhoto:
                personal_details?.passportPhoto || null,

            sebi_action:
                typeof personal_details?.sebi_action === "boolean"
                    ? personal_details.sebi_action
                    : null,

            investment_trading_experience:
                personal_details?.investment_trading_experience || "",

            status:
                personal_details?.status || "",

            is_merchant_navy_nri:
                personal_details?.is_merchant_navy_nri || false,

            merchant_navy_document_type:
                personal_details?.merchant_navy_document_type || "",

            merchant_navy_document:
                personal_details?.merchant_navy_document || null,

            tax_resident_other_country:
                personal_details?.tax_resident_other_country || false,

            tax_residency_details:
                Array.isArray(personal_details?.tax_residency_details)
                    ? personal_details.tax_residency_details
                    : [],
        }));

        setTaxRowErrors([]);
        setTaxSectionError("");
    }, [personal_details]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        if (
            name === "differentlyAbledStatus" &&
            value !== "Differently Abled"
        ) {
            setFormData((prev: any) => ({
                ...prev,
                differentlyAbledStatus: value,
                typeOfImpairment: "",
                percentageOfImpairment: "",
                UDIDNumber: "",
            }));

            return;
        }

        setFormData((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleBooleanRadioChange =
        (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value === "Yes";

            setFormData((prev: any) => {
                const updated = {
                    ...prev,
                    [field]: value,
                };

                if (field === "is_merchant_navy_nri" && !value) {
                    updated.merchant_navy_document_type = "";
                    updated.merchant_navy_document = null;
                }

                if (
                    field === "tax_resident_other_country" &&
                    !value
                ) {
                    updated.tax_residency_details = [];
                }

                return updated;
            });

            if (
                field === "tax_resident_other_country" &&
                !value
            ) {
                setTaxRowErrors([]);
                setTaxSectionError("");
            }
        };

    const handleImageUpload = (field: string) => (e: any) => {
        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        setFormData((prev: any) => ({
            ...prev,
            [field]: file,
        }));
    };

    const getBase64FileInfo = (base64: string) => {
        if (base64.startsWith("JVBERi0")) {
            return {
                mime: "application/pdf",
                ext: "pdf",
                isPdf: true,
            };
        }

        if (base64.startsWith("/9j/")) {
            return {
                mime: "image/jpeg",
                ext: "jpg",
                isPdf: false,
            };
        }

        if (base64.startsWith("iVBORw0KGgo")) {
            return {
                mime: "image/png",
                ext: "png",
                isPdf: false,
            };
        }

        return {
            mime: "application/octet-stream",
            ext: "bin",
            isPdf: false,
        };
    };

    const handleDownloadFile = (
        file: any,
        fileName: string
    ) => {
        if (!file) {
            return;
        }

        let url = "";
        let downloadName = fileName;

        if (file instanceof File) {
            url = URL.createObjectURL(file);
            downloadName = file.name || fileName;
        } else {
            const { mime, ext } = getBase64FileInfo(file);

            url = `data:${mime};base64,${file}`;
            downloadName = `${fileName}.${ext}`;
        }

        const link = document.createElement("a");

        link.href = url;
        link.download = downloadName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (file instanceof File) {
            URL.revokeObjectURL(url);
        }
    };

    const getPreview = (file: any) => {
        if (!file) {
            return "";
        }

        if (file instanceof File) {
            return URL.createObjectURL(file);
        }

        const { mime } = getBase64FileInfo(file);

        return `data:${mime};base64,${file}`;
    };

    const addTaxResidencyRow = () => {
        const taxRows =
            formData.tax_residency_details || [];

        if (taxRows.length >= 3) {
            return;
        }

        setFormData((prev: any) => ({
            ...prev,
            tax_residency_details: [
                ...(prev.tax_residency_details || []),
                { ...emptyTaxResidencyRow },
            ],
        }));

        setTaxRowErrors((prev) => [
            ...prev,
            {},
        ]);

        setTaxSectionError("");
    };

    const removeTaxResidencyRow = (index: number) => {
        setFormData((prev: any) => ({
            ...prev,
            tax_residency_details:
                (prev.tax_residency_details || []).filter(
                    (_: any, i: number) => i !== index
                ),
        }));

        setTaxRowErrors((prev) =>
            prev.filter(
                (_: any, i: number) => i !== index
            )
        );

        setTaxSectionError("");
    };

    const updateTaxResidencyRow = (
        index: number,
        key: string,
        value: any
    ) => {
        setFormData((prev: any) => ({
            ...prev,
            tax_residency_details:
                (prev.tax_residency_details || []).map(
                    (row: any, i: number) =>
                        i === index
                            ? {
                                ...row,
                                [key]: value,
                            }
                            : row
                ),
        }));

        setTaxRowErrors((prev) => {
            const next = [...prev];

            if (next[index]) {
                next[index] = {
                    ...next[index],
                    [key]: undefined,
                };
            }

            return next;
        });

        setTaxSectionError("");
    };

    const validateNRI = () => {
        if (!isNRI) {
            return true;
        }

        if (
            formData.sebi_action === null ||
            formData.sebi_action === undefined
        ) {
            toastr.error(
                "Please select SEBI Action."
            );

            return false;
        }

        if (
            !formData.investment_trading_experience
        ) {
            toastr.error(
                "Please enter Investment / Trading Experience."
            );

            return false;
        }

        if (
            !/^\d+$/.test(
                String(
                    formData.investment_trading_experience
                )
            )
        ) {
            toastr.error(
                "Investment / Trading Experience should contain only years."
            );

            return false;
        }

        if (!formData.status) {
            toastr.error(
                "Please select Status."
            );

            return false;
        }

        if (
            formData.is_merchant_navy_nri &&
            !formData.merchant_navy_document_type
        ) {
            toastr.error(
                "Please select Merchant Navy Document Type."
            );

            return false;
        }

        if (
            formData.is_merchant_navy_nri &&
            !formData.merchant_navy_document
        ) {
            toastr.error(
                "Please upload Merchant Navy Document."
            );

            return false;
        }

        if (formData.tax_resident_other_country) {
            const taxRows =
                formData.tax_residency_details || [];

            if (taxRows.length === 0) {
                toastr.error(
                    "Please add at least one Tax Residency Detail."
                );

                return false;
            }

            if (taxRows.length > 3) {
                toastr.error(
                    "Maximum 3 Tax Residency Details are allowed."
                );

                return false;
            }

            const errors = taxRows.map(
                (row: any) =>
                    validateTaxRow(row)
            );

            const hasErrors = errors.some(
                (error: TaxRowErrors) =>
                    Object.keys(error).length > 0
            );

            if (hasErrors) {
                setTaxRowErrors(errors);

                toastr.error(
                    "Please complete all Tax Residency Details."
                );

                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateNRI()) {
            return;
        }

        try {
            setLoading(true);

            const form = new FormData();

            form.append(
                "user_type",
                user_type
            );

            form.append(
                "userUUID",
                currentUser.uuid
            );

            form.append(
                "maritalStatus",
                formData.maritalStatus || ""
            );

            form.append(
                "maidenName",
                formData.maidenName || ""
            );

            form.append(
                "gender",
                formData.gender || ""
            );

            form.append(
                "occupation",
                formData.occupation || ""
            );

            form.append(
                "politicallyExposed",
                formData.politicallyExposed === "Yes"
                    ? "true"
                    : "false"
            );

            form.append(
                "annualIncome",
                formData.annualIncome || ""
            );

            form.append(
                "motherName",
                formData.motherName || ""
            );

            form.append(
                "qualification",
                formData.qualification || ""
            );

            form.append(
                "differentlyAbledStatus",
                formData.differentlyAbledStatus || ""
            );

            form.append(
                "typeOfImpairment",
                formData.typeOfImpairment || ""
            );

            form.append(
                "percentageOfImpairment",
                formData.percentageOfImpairment || ""
            );

            form.append(
                "UDIDNumber",
                formData.UDIDNumber || ""
            );

            form.append(
                "birthCity",
                formData.birthCity || ""
            );

            form.append(
                "birthCountry",
                formData.birthCountry || ""
            );

            form.append(
                "nationality",
                formData.nationality || ""
            );

            if (
                formData.wetSignImage instanceof File
            ) {
                form.append(
                    "photopart",
                    formData.wetSignImage
                );
            }

            if (
                formData.passportPhoto instanceof File
            ) {
                form.append(
                    "signpart",
                    formData.passportPhoto
                );
            }

            if (isNRI) {
                form.append(
                    "sebi_action",
                    String(
                        formData.sebi_action
                    )
                );

                form.append(
                    "investment_trading_experience",
                    formData.investment_trading_experience || ""
                );

                form.append(
                    "status",
                    formData.status || ""
                );

                form.append(
                    "is_merchant_navy_nri",
                    String(
                        !!formData.is_merchant_navy_nri
                    )
                );

                form.append(
                    "merchant_navy_document_type",
                    formData.merchant_navy_document_type || ""
                );

                if (
                    formData.merchant_navy_document instanceof File
                ) {
                    form.append(
                        "merchant_navy_document",
                        formData.merchant_navy_document
                    );
                }

                form.append(
                    "tax_resident_other_country",
                    String(
                        !!formData.tax_resident_other_country
                    )
                );

                form.append(
                    "tax_residency_details",
                    JSON.stringify(
                        formData.tax_resident_other_country
                            ? (
                                formData.tax_residency_details ||
                                []
                            )
                            : []
                    )
                );
            }

            const resp =
                await makePUTRequestOnUserService(
                    `/partneruser/additional_details/update/`,
                    form
                );

            if (resp.status === 200) {
                await fetchKycDocs();

                toastr.success(
                    "Personal Details Updated"
                );

                setValue(
                    (prev: number) =>
                        prev + 1
                );
            }
        } catch (err: any) {
            console.error(
                "Personal Details Update Error:",
                err
            );

            toastr.error(
                getErrorMessage(err)
            );
        } finally {
            setLoading(false);
        }
    };

    const taxRows =
        formData.tax_residency_details || [];

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        select
                        label="Differently Abled Status"
                        name="differentlyAbledStatus"
                        value={
                            formData.differentlyAbledStatus
                        }
                        onChange={handleChange}
                    >
                        {differentlyAbledStatus.map(
                            (item, i) => (
                                <MenuItem
                                    key={i}
                                    value={item}
                                >
                                    {item}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                </Grid>

                {formData.differentlyAbledStatus ===
                    "Differently Abled" && (
                        <>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Type Of Impairment"
                                    name="typeOfImpairment"
                                    value={
                                        formData.typeOfImpairment
                                    }
                                    onChange={handleChange}
                                >
                                    {typeOfImpairment.map(
                                        (item, i) => (
                                            <MenuItem
                                                key={i}
                                                value={item}
                                            >
                                                {item}
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            </Grid>

                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label="Percentage Of Impairment"
                                    name="percentageOfImpairment"
                                    value={
                                        formData.percentageOfImpairment
                                    }
                                    onChange={(e) => {
                                        let value =
                                            e.target.value;

                                        if (
                                            /^\d{0,3}(\.\d{0,2})?$/.test(
                                                value
                                            ) ||
                                            value === ""
                                        ) {
                                            let num =
                                                parseFloat(
                                                    value
                                                );

                                            if (
                                                !isNaN(num) &&
                                                num > 100
                                            ) {
                                                num = 100;
                                            }

                                            setFormData(
                                                (
                                                    prev: any
                                                ) => ({
                                                    ...prev,
                                                    percentageOfImpairment:
                                                        value ===
                                                            ""
                                                            ? ""
                                                            : num.toString(),
                                                })
                                            );
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                %
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label="UDID Number"
                                    name="UDIDNumber"
                                    value={
                                        formData.UDIDNumber
                                    }
                                    onChange={handleChange}
                                />
                            </Grid>
                        </>
                    )}

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        select
                        label="Marital Status"
                        name="maritalStatus"
                        value={
                            formData.maritalStatus
                        }
                        onChange={handleChange}
                    >
                        <MenuItem value="Single">
                            Single
                        </MenuItem>

                        <MenuItem value="Married">
                            Married
                        </MenuItem>
                    </TextField>
                </Grid>

                <Grid item xs={3}>
                    <Typography>
                        Politically Exposed?
                    </Typography>

                    <RadioGroup
                        row
                        name="politicallyExposed"
                        value={
                            formData.politicallyExposed
                        }
                        onChange={handleChange}
                    >
                        <FormControlLabel
                            value="Yes"
                            control={<Radio />}
                            label="Yes"
                        />

                        <FormControlLabel
                            value="No"
                            control={<Radio />}
                            label="No"
                        />
                    </RadioGroup>
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        label="Maiden Name"
                        name="maidenName"
                        value={
                            formData.maidenName
                        }
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        label="Mother Name"
                        name="motherName"
                        value={
                            formData.motherName
                        }
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        select
                        label="Annual Income"
                        name="annualIncome"
                        value={
                            formData.annualIncome
                        }
                        onChange={handleChange}
                    >
                        {incomeRanges.map(
                            (item, i) => (
                                <MenuItem
                                    key={i}
                                    value={item}
                                >
                                    {item}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        select
                        label="Qualification"
                        name="qualification"
                        value={
                            formData.qualification
                        }
                        onChange={handleChange}
                    >
                        {qualification.map(
                            (item, i) => (
                                <MenuItem
                                    key={i}
                                    value={item}
                                >
                                    {item}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        select
                        label="Gender"
                        name="gender"
                        value={
                            formData.gender
                        }
                        onChange={handleChange}
                    >
                        <MenuItem value="Male">
                            Male
                        </MenuItem>

                        <MenuItem value="Female">
                            Female
                        </MenuItem>

                        <MenuItem value="Transgender">
                            Transgender
                        </MenuItem>
                    </TextField>
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        label="Occupation"
                        name="occupation"
                        value={
                            formData.occupation
                        }
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        label="Birth City"
                        name="birthCity"
                        value={
                            formData.birthCity
                        }
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        label="Birth Country"
                        name="birthCountry"
                        value={
                            formData.birthCountry
                        }
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        fullWidth
                        label="Nationality"
                        name="nationality"
                        value={
                            formData.nationality
                        }
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Grid container spacing={2}>

                        {/* Wet Signature */}

                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "space-between",
                                    mb: 1,
                                }}
                            >
                                <Typography>
                                    Wet Signature
                                </Typography>

                                {formData.wetSignImage && (
                                    <IconButton
                                        size="small"
                                        sx={{
                                            color: "green",
                                        }}
                                        onClick={() =>
                                            handleDownloadFile(
                                                formData.wetSignImage,
                                                "wet-signature"
                                            )
                                        }
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>

                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload(
                                    "wetSignImage"
                                )}
                                style={{
                                    marginBottom: "10px",
                                }}
                            />

                            <Box
                                sx={{
                                    height: 160,
                                    border:
                                        "1px dashed #ccc",
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    borderRadius: 2,
                                    background:
                                        "#fafafa",
                                    overflow:
                                        "hidden",
                                }}
                            >
                                {formData.wetSignImage ? (
                                    <img
                                        src={getPreview(
                                            formData.wetSignImage
                                        )}
                                        style={{
                                            width:
                                                "100%",
                                            height:
                                                "100%",
                                            objectFit:
                                                "contain",
                                        }}
                                    />
                                ) : (
                                    "No Preview"
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "space-between",
                                    mb: 1,
                                }}
                            >
                                <Typography>
                                    Passport Photo
                                </Typography>

                                {formData.passportPhoto && (
                                    <IconButton
                                        size="small"
                                        sx={{
                                            color: "green",
                                        }}
                                        onClick={() =>
                                            handleDownloadFile(
                                                formData.passportPhoto,
                                                "passport-photo"
                                            )
                                        }
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>

                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload(
                                    "passportPhoto"
                                )}
                                style={{
                                    marginBottom: "10px",
                                }}
                            />

                            <Box
                                sx={{
                                    height: 160,
                                    border:
                                        "1px dashed #ccc",
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    borderRadius: 2,
                                    background:
                                        "#fafafa",
                                    overflow:
                                        "hidden",
                                }}
                            >
                                {formData.passportPhoto ? (
                                    <img
                                        src={getPreview(
                                            formData.passportPhoto
                                        )}
                                        style={{
                                            width:
                                                "100%",
                                            height:
                                                "100%",
                                            objectFit:
                                                "contain",
                                        }}
                                    />
                                ) : (
                                    "No Preview"
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                {isNRI && (
                    <>
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    borderTop:
                                        "1px solid #ddd",
                                    mt: 2,
                                    pt: 3,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 2,
                                    }}
                                >
                                    NRI Details
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={4}>
                            <Typography>
                                Have you ever been subject
                                to SEBI action?
                            </Typography>

                            <RadioGroup
                                row
                                value={
                                    formData.sebi_action ===
                                        true
                                        ? "Yes"
                                        : formData.sebi_action ===
                                            false
                                            ? "No"
                                            : ""
                                }
                                onChange={(
                                    e
                                ) => {
                                    setFormData(
                                        (
                                            prev: any
                                        ) => ({
                                            ...prev,
                                            sebi_action:
                                                e
                                                    .target
                                                    .value ===
                                                "Yes",
                                        })
                                    );
                                }}
                            >
                                <FormControlLabel
                                    value="Yes"
                                    control={
                                        <Radio />
                                    }
                                    label="Yes"
                                />

                                <FormControlLabel
                                    value="No"
                                    control={
                                        <Radio />
                                    }
                                    label="No"
                                />
                            </RadioGroup>
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Investment / Trading Experience (Years)"
                                type="number"
                                value={
                                    formData.investment_trading_experience
                                }
                                onChange={(e) => {
                                    const value =
                                        e.target.value;

                                    if (
                                        /^\d*$/.test(
                                            value
                                        )
                                    ) {
                                        setFormData(
                                            (
                                                prev: any
                                            ) => ({
                                                ...prev,
                                                investment_trading_experience:
                                                    value,
                                            })
                                        );
                                    }
                                }}
                                inputProps={{
                                    min: 0,
                                    max: 99,
                                }}
                            />
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                select
                                label="Status"
                                value={
                                    formData.status
                                }
                                onChange={(e) =>
                                    setFormData(
                                        (
                                            prev: any
                                        ) => ({
                                            ...prev,
                                            status:
                                                e
                                                    .target
                                                    .value,
                                        })
                                    )
                                }
                            >
                                <MenuItem value="resident_individual">
                                    Resident Individual
                                </MenuItem>

                                <MenuItem value="non_resident_individual">
                                    Non Resident Individual
                                </MenuItem>

                                <MenuItem value="foreign_national">
                                    Foreign National
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={4}>
                            <Typography>
                                Are you Merchant Navy
                                NRI?
                            </Typography>

                            <RadioGroup
                                row
                                value={
                                    formData.is_merchant_navy_nri
                                        ? "Yes"
                                        : "No"
                                }
                                onChange={handleBooleanRadioChange(
                                    "is_merchant_navy_nri"
                                )}
                            >
                                <FormControlLabel
                                    value="Yes"
                                    control={
                                        <Radio />
                                    }
                                    label="Yes"
                                />

                                <FormControlLabel
                                    value="No"
                                    control={
                                        <Radio />
                                    }
                                    label="No"
                                />
                            </RadioGroup>
                        </Grid>

                        {formData.is_merchant_navy_nri && (
                            <>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Document Type"
                                        value={
                                            formData.merchant_navy_document_type
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            setFormData(
                                                (
                                                    prev: any
                                                ) => ({
                                                    ...prev,
                                                    merchant_navy_document_type:
                                                        e
                                                            .target
                                                            .value,
                                                })
                                            )
                                        }
                                    >
                                        <MenuItem value="mariner_declaration">
                                            Mariner Declaration
                                        </MenuItem>

                                        <MenuItem value="cdc">
                                            CDC
                                        </MenuItem>
                                    </TextField>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography
                                        sx={{
                                            mb: 1,
                                        }}
                                    >
                                        Merchant Navy
                                        Document
                                    </Typography>

                                    <Box
                                        sx={{
                                            display:
                                                "flex",
                                            gap: 1,
                                            alignItems:
                                                "center",
                                        }}
                                    >
                                        <input
                                            type="file"
                                            accept="image/*,.pdf,application/pdf"
                                            onChange={handleImageUpload(
                                                "merchant_navy_document"
                                            )}
                                        />

                                        {formData.merchant_navy_document && (
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    color:
                                                        "green",
                                                }}
                                                onClick={() =>
                                                    handleDownloadFile(
                                                        formData.merchant_navy_document,
                                                        "merchant-navy-document"
                                                    )
                                                }
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                        )}
                                    </Box>

                                    {formData.merchant_navy_document && (
                                        <Box
                                            sx={{
                                                mt: 2,
                                                height: 220,
                                                border:
                                                    "1px solid #ddd",
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                overflow:
                                                    "hidden",
                                            }}
                                        >
                                            {formData
                                                .merchant_navy_document instanceof
                                                File ? (
                                                formData
                                                    .merchant_navy_document
                                                    .type ===
                                                    "application/pdf" ? (
                                                    <iframe
                                                        src={getPreview(
                                                            formData.merchant_navy_document
                                                        )}
                                                        title="Merchant Navy Document"
                                                        width="100%"
                                                        height="100%"
                                                    />
                                                ) : (
                                                    <img
                                                        src={getPreview(
                                                            formData.merchant_navy_document
                                                        )}
                                                        alt="Merchant Navy Document"
                                                        style={{
                                                            width:
                                                                "100%",
                                                            height:
                                                                "100%",
                                                            objectFit:
                                                                "contain",
                                                        }}
                                                    />
                                                )
                                            ) : formData.merchant_navy_document.startsWith(
                                                "JVBERi0"
                                            ) ? (
                                                <iframe
                                                    src={getPreview(
                                                        formData.merchant_navy_document
                                                    )}
                                                    title="Merchant Navy Document"
                                                    width="100%"
                                                    height="100%"
                                                />
                                            ) : (
                                                <img
                                                    src={getPreview(
                                                        formData.merchant_navy_document
                                                    )}
                                                    alt="Merchant Navy Document"
                                                    style={{
                                                        width:
                                                            "100%",
                                                        height:
                                                            "100%",
                                                        objectFit:
                                                            "contain",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </Grid>
                            </>
                        )}

                        <Grid item xs={4}>
                            <Typography>
                                Tax Resident of another
                                country?
                            </Typography>

                            <RadioGroup
                                row
                                value={
                                    formData.tax_resident_other_country
                                        ? "Yes"
                                        : "No"
                                }
                                onChange={handleBooleanRadioChange(
                                    "tax_resident_other_country"
                                )}
                            >
                                <FormControlLabel
                                    value="Yes"
                                    control={
                                        <Radio />
                                    }
                                    label="Yes"
                                />

                                <FormControlLabel
                                    value="No"
                                    control={
                                        <Radio />
                                    }
                                    label="No"
                                />
                            </RadioGroup>
                        </Grid>

                        {formData.tax_resident_other_country && (
                            <Grid item xs={12}>
                                <TableContainer
                                    sx={{
                                        mt: 2,
                                        overflowX:
                                            "auto",
                                    }}
                                >
                                    <Table
                                        size="small"
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    Account
                                                    Holder
                                                    Name
                                                </TableCell>

                                                <TableCell>
                                                    Account
                                                    Holder
                                                    Details
                                                </TableCell>

                                                <TableCell>
                                                    Country
                                                    of Tax
                                                    Residency
                                                </TableCell>

                                                <TableCell>
                                                    Tax
                                                    Identification
                                                    Number
                                                </TableCell>

                                                <TableCell>
                                                    Identification
                                                    Type
                                                </TableCell>

                                                <TableCell>
                                                    US Citizen /
                                                    Green Card
                                                    Holder?
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                >
                                                    <Tooltip
                                                        title={
                                                            taxRows.length >=
                                                                3
                                                                ? "Maximum 3 entries allowed"
                                                                : "Add tax residency detail"
                                                        }
                                                    >
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                disabled={
                                                                    taxRows.length >=
                                                                    3
                                                                }
                                                                onClick={
                                                                    addTaxResidencyRow
                                                                }
                                                            >
                                                                <AddCircleOutlineIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {taxRows.length ===
                                                0 && (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={
                                                                7
                                                            }
                                                            align="center"
                                                        >
                                                            No tax
                                                            residency
                                                            details
                                                            added.
                                                            Click +
                                                            to add
                                                            one.
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                            {taxRows.map(
                                                (
                                                    row: any,
                                                    index: number
                                                ) => {
                                                    const errors =
                                                        taxRowErrors[
                                                        index
                                                        ] ||
                                                        {};

                                                    return (
                                                        <TableRow
                                                            key={
                                                                index
                                                            }
                                                        >
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={
                                                                        row.account_holder_name
                                                                    }
                                                                    error={!!errors.account_holder_name}
                                                                    helperText={
                                                                        errors.account_holder_name
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateTaxResidencyRow(
                                                                            index,
                                                                            "account_holder_name",
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                />
                                                            </TableCell>

                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={
                                                                        row.account_holder_details
                                                                    }
                                                                    error={!!errors.account_holder_details}
                                                                    helperText={
                                                                        errors.account_holder_details
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateTaxResidencyRow(
                                                                            index,
                                                                            "account_holder_details",
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                />
                                                            </TableCell>

                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={
                                                                        row.country_of_tax_residency
                                                                    }
                                                                    error={!!errors.country_of_tax_residency}
                                                                    helperText={
                                                                        errors.country_of_tax_residency
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateTaxResidencyRow(
                                                                            index,
                                                                            "country_of_tax_residency",
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                />
                                                            </TableCell>

                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={
                                                                        row.tax_identification_number
                                                                    }
                                                                    error={!!errors.tax_identification_number}
                                                                    helperText={
                                                                        errors.tax_identification_number
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateTaxResidencyRow(
                                                                            index,
                                                                            "tax_identification_number",
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                />
                                                            </TableCell>

                                                            <TableCell>
                                                                <TextField
                                                                    select
                                                                    fullWidth
                                                                    size="small"
                                                                    value={
                                                                        row.identification_type
                                                                    }
                                                                    error={!!errors.identification_type}
                                                                    helperText={
                                                                        errors.identification_type
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateTaxResidencyRow(
                                                                            index,
                                                                            "identification_type",
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                >
                                                                    {identificationTypes.map(
                                                                        (
                                                                            item,
                                                                            i
                                                                        ) => (
                                                                            <MenuItem
                                                                                key={
                                                                                    i
                                                                                }
                                                                                value={
                                                                                    item
                                                                                }
                                                                            >
                                                                                {
                                                                                    item
                                                                                }
                                                                            </MenuItem>
                                                                        )
                                                                    )}
                                                                </TextField>
                                                            </TableCell>

                                                            <TableCell>
                                                                <RadioGroup
                                                                    row
                                                                    value={
                                                                        row.is_us_citizen_or_green_card_holder
                                                                            ? "Yes"
                                                                            : "No"
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateTaxResidencyRow(
                                                                            index,
                                                                            "is_us_citizen_or_green_card_holder",
                                                                            e
                                                                                .target
                                                                                .value ===
                                                                            "Yes"
                                                                        )
                                                                    }
                                                                >
                                                                    <FormControlLabel
                                                                        value="Yes"
                                                                        control={
                                                                            <Radio size="small" />
                                                                        }
                                                                        label="Yes"
                                                                    />

                                                                    <FormControlLabel
                                                                        value="No"
                                                                        control={
                                                                            <Radio size="small" />
                                                                        }
                                                                        label="No"
                                                                    />
                                                                </RadioGroup>
                                                            </TableCell>

                                                            <TableCell align="center">
                                                                <Tooltip title="Remove">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() =>
                                                                            removeTaxResidencyRow(
                                                                                index
                                                                            )
                                                                        }
                                                                    >
                                                                        <DeleteOutlineIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                }
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        {taxRows.length >= 3 && (
                            <Grid item xs={12}>
                                <Typography
                                    sx={{
                                        textAlign:
                                            "right",
                                        color:
                                            "#999",
                                        fontSize: 12,
                                    }}
                                >
                                    Maximum of 3 tax
                                    residency details
                                    reached.
                                </Typography>
                            </Grid>
                        )}

                        {taxSectionError && (
                            <Grid item xs={12}>
                                <Typography
                                    color="error"
                                    fontSize={13}
                                >
                                    {taxSectionError}
                                </Typography>
                            </Grid>
                        )}
                    </>
                )}

                <Grid
                    item
                    xs={12}
                    sx={{
                        textAlign: "center",
                        mt: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{
                            backgroundColor:
                                "#262349",
                            "&:hover": {
                                backgroundColor:
                                    "#1d1a3a",
                            },
                        }}
                    >
                        {loading
                            ? "Saving..."
                            : "Save"}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};